'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function YuzeDolguYapanDoktorlar() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/yuze-dolgu-yapan-doktorlar-hangi-brans-yetkilidir-nasil-secim-yapilmali';
  const pageTitle = 'Yüze Dolgu Yapan Doktorlar: Hangi Branş Yetkilidir, Nasıl Seçim Yapılmalı?';
  const pageDescription = 'Yüze dolgu işlemini kim yapabilir? Hangi doktorlar yasal olarak dolgu uygulaması yapma yetkisine sahiptir? Uzmanlık farkları, güvenli seçim kriterleri ve klinik uyarılar.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Yüze Dolgu Yapan Doktorlar', href: '/tr/blog/yuze-dolgu-yapan-doktorlar-hangi-brans-yetkilidir-nasil-secim-yapilmali' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Yüze dolgu yapan doktorlar ve branş yetkileri"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-violet-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Doktor Seçimi
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Yüze Dolgu Yapan Doktorlar: Hangi Branş Yetkilidir, Nasıl Seçim Yapılmalı?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>12 Mart 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>15 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Yüz dolgusu, estetik tıbbın en hassas alanlarından biridir.
                Doğal ve dengeli bir yüz ifadesi elde etmek yalnızca doğru ürünle değil, doğru hekimle mümkündür.
              </p>

              <div className="bg-violet-50 border-l-4 border-violet-400 p-6 mb-6">
                <p className="text-violet-800 font-medium text-lg">
                  "Dolgu bir iğne değil, anatomi bilgisidir."
                </p>
                <p className="text-violet-800 font-medium mt-2">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣ Yüz Dolgusu Nedir?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolgu, cilt altına hyaluronik asit veya benzeri maddelerin enjekte edilmesiyle yapılan hacim kazandırıcı işlemdir.
                Uygulama sırasında damar, sinir, kas ve kemik yapısının derin bilgisi gerekir.
                Bu nedenle yüz dolgusu yalnızca tıp eğitimi almış ve uygulama yetkisine sahip doktorlar tarafından yapılabilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2️⃣ Kimler Dolgu Yapabilir?
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-violet-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Branş</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Yasal Yetki</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Plastik, Rekonstrüktif ve Estetik Cerrahi Uzmanı</td>
                      <td className="border border-gray-300 px-4 py-3 text-green-600 font-medium">✅ Evet</td>
                      <td className="border border-gray-300 px-4 py-3">Yüz anatomi ve doku bilgisi en yüksek branştır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Dermatoloji Uzmanı (Cildiye)</td>
                      <td className="border border-gray-300 px-4 py-3 text-green-600 font-medium">✅ Evet</td>
                      <td className="border border-gray-300 px-4 py-3">Cilt yapısı ve dolgu davranışı konusunda uzmandır</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Medikal Estetik Hekimi (Eğitimli Doktor)</td>
                      <td className="border border-gray-300 px-4 py-3 text-green-600 font-medium">✅ Evet</td>
                      <td className="border border-gray-300 px-4 py-3">Tıp fakültesi mezunu olup ek sertifikasyon almış hekimdir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Aile Hekimi veya Diğer Branş Hekimi</td>
                      <td className="border border-gray-300 px-4 py-3 text-yellow-600 font-medium">🔸 Kısmen</td>
                      <td className="border border-gray-300 px-4 py-3">Sadece medikal estetik eğitimi ve sertifikası varsa</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hemşire / Kozmetolog / Estetisyen</td>
                      <td className="border border-gray-300 px-4 py-3 text-red-600 font-medium">❌ Hayır</td>
                      <td className="border border-gray-300 px-4 py-3">Enjeksiyon yetkisi yoktur</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  📍 Sonuç: Dolgu işlemi yalnızca doktor tarafından yapılabilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3️⃣ Hangi Doktor Daha Uygun?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Yüz dolgusunda "en iyi branş" değil, "en doğru yaklaşım" önemlidir.
                Her doktorun güçlü olduğu alan farklıdır:
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-blue-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Branş</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Güçlü Yön</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Plastik cerrah</td>
                      <td className="border border-gray-300 px-4 py-3">Simetri, hacim ve lifting uygulamaları</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Dermatolog</td>
                      <td className="border border-gray-300 px-4 py-3">Cilt kalitesi ve doğal görünüm</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Medikal estetik hekimi</td>
                      <td className="border border-gray-300 px-4 py-3">Denge ve dolgu-botoks kombinasyonları</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4️⃣ Dolgu Deneyimi Nasıl Anlaşılır?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                İşlem öncesi şunları mutlaka sor:
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-green-800">
                  <li>Kaç yıldır dolgu uygulaması yapıyor?</li>
                  <li>Kullandığı dolgu markaları hangileri?</li>
                  <li>Kendi klinik ortamında mı uygulama yapıyor?</li>
                  <li>İşlem sonrası kontrol sağlıyor mu?</li>
                </ul>
                <p className="text-green-800 mt-4 font-medium">
                  Bu sorular, hem güven hem kalite göstergesidir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5️⃣ Klinik Ortamın Önemi
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Yüz dolgusu tıbbi bir işlemdir; steril koşul zorunludur.
                Evde, güzellik merkezinde veya otel odasında yapılan enjeksiyonlar hem yasal hem tıbbi olarak geçersizdir.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <p className="text-red-800 font-medium">
                  "Dolgu steril olmazsa güzellik değil, enfeksiyon olur."
                </p>
                <p className="text-red-800 font-medium mt-2">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6️⃣ Deneyimsiz Uygulayıcıların Yarattığı Sorunlar
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-red-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Sorun</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Asimetri</td>
                      <td className="border border-gray-300 px-4 py-3">Yanlış nokta veya doz</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Damar tıkanması</td>
                      <td className="border border-gray-300 px-4 py-3">Anatomik bilgi eksikliği</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Alerji / morarma</td>
                      <td className="border border-gray-300 px-4 py-3">Derinlik hatası</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sinir hasarı</td>
                      <td className="border border-gray-300 px-4 py-3">Yanlış enjeksiyon hattı</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu komplikasyonların önlenmesi ancak deneyimli doktorlarla mümkündür.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7️⃣ Uzman Seçerken Dikkat Edilmesi Gerekenler
              </h2>
              
              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-blue-800">
                  <li>👨‍⚕️ Branşını mutlaka öğren.</li>
                  <li>🏥 Muayenehane lisansına bak.</li>
                  <li>💉 Ürün kutusunu ve barkodunu iste.</li>
                  <li>🕒 Kontrol randevusu veriyor mu kontrol et.</li>
                  <li>🧾 Bilgilendirilmiş onam formu imzalanmalı.</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8️⃣ Estetikte Doktorun Rolü
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Yüz dolgusunun başarısı, teknik kadar sanatsal bakış da gerektirir.
                Hekim yüz oranlarını, ışık-gölge dengesini ve doğal ifadeyi birlikte değerlendirir.
                Bu yüzden "doktor eli" sadece tıbbi değil, estetik bir dokunuştur.
              </p>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-4 mb-6">
                <p className="text-purple-800 font-medium">
                  "En iyi dolgu, fark edilmeyendir."
                </p>
                <p className="text-purple-800 font-medium mt-2">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                9️⃣ Hangi Doktor Size Uygun?
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-green-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Hedefiniz</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Tercih Edilmesi Gereken Branş</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Yüz hatlarını yeniden şekillendirmek</td>
                      <td className="border border-gray-300 px-4 py-3">Plastik Cerrah</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">İnce kırışıklıkları gidermek</td>
                      <td className="border border-gray-300 px-4 py-3">Dermatolog</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Dudak / nazolabial denge sağlamak</td>
                      <td className="border border-gray-300 px-4 py-3">Medikal Estetik Hekimi</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                🔟 Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Aile hekimi dolgu yapabilir mi?</h3>
                  <p className="text-gray-600">Sadece medikal estetik sertifikası varsa, evet.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. En iyi branş hangisi?</h3>
                  <p className="text-gray-600">Yüz yapınıza ve beklentinize göre değişir; plastik cerrah ve dermatolog önceliklidir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Estetisyen dolgu yapabilir mi?</h3>
                  <p className="text-gray-600">Hayır, enjeksiyon yetkisi yoktur.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Doktoru nasıl doğrularım?</h3>
                  <p className="text-gray-600">Sağlık Bakanlığı "Hekim Bilgi Bankası" sisteminden kontrol edebilirsin.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Dolgu işlemi ne kadar sürer?</h3>
                  <p className="text-gray-600">Uzman hekim tarafından yapıldığında 15-30 dakika arası sürer.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Hangi dolgu markaları güvenli?</h3>
                  <p className="text-gray-600">Juvederm, Restylane, Teosyal gibi Sağlık Bakanlığı onaylı markalar güvenlidir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣1️⃣ Etik ve Klinik Değerlendirme
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolgu işlemi yalnızca hekim yetkisindedir.
                Yetkisiz uygulamalar sadece estetik değil, hukuki risk de doğurur.
              </p>

              <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 mb-6">
                <p className="text-indigo-800 font-medium">
                  💬 "Güzellik doktorla başlar, bilinçle korunur."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣2️⃣ Sonuç ve Klinik Öneri
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Yüz dolgusunu yapan kişi, işlemin başarısını belirler.
                Yasal, deneyimli ve etik çalışan bir doktor seçmek; doğal sonuç kadar güvenli geleceği de sağlar.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
                <p className="text-red-800 font-medium">
                  🩺 Dr. Barış Çin öneriyor:
                </p>
                <p className="text-red-800 mt-2">
                  "Yüzüne dokunacak kişiyi iyi seç.
                  Çünkü her iğne bir sanattır — ama yanlış elde bir risktir."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>1219 Sayılı Tababet Kanunu</li>
                  <li>Sağlık Bakanlığı Yetkili Branşlar Listesi (2024)</li>
                  <li>TETD (2024) – "Dolgu Uygulama Yetki Rehberi"</li>
                  <li>ISAPS (2023) – "Qualified Injector Standards Report"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-violet-100 text-violet-800 px-3 py-1 rounded-full text-sm">#yüzdolgu</span>
                <span className="bg-violet-100 text-violet-800 px-3 py-1 rounded-full text-sm">#DrBarışÇin</span>
                <span className="bg-violet-100 text-violet-800 px-3 py-1 rounded-full text-sm">#estetikdoktor</span>
                <span className="bg-violet-100 text-violet-800 px-3 py-1 rounded-full text-sm">#yasaluygulama</span>
                <span className="bg-violet-100 text-violet-800 px-3 py-1 rounded-full text-sm">#güzellikvebilinç</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-violet-600 to-purple-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Güvenli Dolgu Uygulaması İçin Randevu Alın</h3>
          <p className="mb-6">Uzman doktorumuzdan güvenli ve yasal dolgu uygulaması hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Güvenli dolgu uygulaması hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-violet-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/orijinal-dolgu-anlama-yontemleri-sahte-urunlerden-korunma"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Orijinal Dolguyu Anlama Yöntemleri</h4>
              <p className="text-gray-600 text-sm">Sahte ürünlerden nasıl korunulur? Orijinal dolgu ürünlerini tanıma rehberi.</p>
            </a>
            <a
              href="/tr/blog/guzellik-merkezlerinin-botoks-yapma-yetkisi-yasal-durum-ve-klinik-gercekler"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Güzellik Merkezlerinin Botoks Yapma Yetkisi</h4>
              <p className="text-gray-600 text-sm">Güzellik merkezleri botoks yapabilir mi? Yasal durum ve klinik gerçekler.</p>
            </a>
            <a
              href="/tr/blog/yuz-dolgusu-sonrasi-dikkat-edilmesi-gerekenler-bilimsel-ve-klinik-rehber"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Yüz Dolgusu Sonrası Dikkat Edilmesi Gerekenler</h4>
              <p className="text-gray-600 text-sm">Yüz dolgusu sonrasında nelere dikkat edilmelidir? Bilimsel ve klinik rehber.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-violet-600 text-white rounded-full hover:bg-violet-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}